import React from 'react';
import { useSelector } from 'react-redux';

import { StandardButton } from 'components/ui/Button';
import { useModal } from 'components/ui/Modals';

import api from 'api';
import { WorkflowTypesMap } from 'components/ApprovalWorkflows/constants';
import permissions from 'constants/permissions';
import { useMutation } from 'react-query';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import useHasPermission from 'utils/useHasPermission';
import { useGetComplianceActionDetails } from '../ComplianceActions/hooks/useGetComplianceActionDetails';
import { isCompliancePaymentInProgress } from '../ComplianceActions/utils';
import ExecutePayroll from '../Popups/ExecutePayroll';
import FinalizePayroll from '../Popups/FinalizePayroll';
import ResumeExecution from '../Popups/ResumeExecution';
import UnfinalizePayroll from '../Popups/UnfinalizePayroll';
import { useRunPayrollDataContext } from '../contexts/RunPayrollDataContext';
import { useErrorMessageContext } from '../contexts/RunPayrollErrorContext';
import { useMessageContext } from '../contexts/RunPayrollMessageContext';
import { useIsTdsFilingConfigRequired } from '../hooks/useIsTdsFilingConfigRequired';
import useNewJoinerArrearEnabled from '../utils/useNewJoinerArrearEnabled';

import ExecuteCard from './ExecuteCard';

import FinalizeCard from './FinalizeCard';

import { useIsPayPtEnabled } from '../ComplianceActions/hooks/useIsPayPtEnabled';
import ExecutionWarningsCard from './ExecutionWarningsCard';

const PayrollActions = () => {
  const { openModal } = useModal();
  const data = useRunPayrollDataContext();
  const errCtx = useErrorMessageContext();
  const message = useMessageContext();
  const { isNewJoinerArrearFeatureEnabled } = useNewJoinerArrearEnabled();

  const isAuthorizedToExecute = useHasPermission([permissions.PAYROLL_EXECUTION]);
  const isAuthorizedToFinalize = useHasPermission([permissions.PAYROLL_FINALIZE]);
  const isOrgKycRestricted = useSelector(loggedInUserSelectors.isOrgKycRestricted);
  const specialPlatformPermissions = useSelector(loggedInUserSelectors.specialPlatformPermissions);
  const orgId = useSelector(loggedInUserSelectors.organizationID);

  const actionsWithActiveWorkflows = useSelector(loggedInUserSelectors.actionsWithActiveWorkflows);
  const features = useSelector(loggedInUserSelectors.features);
  const isAsyncFinalizeEnabled = !!features?.['asyncPayrollFinalize'];
  const isReArchAsyncFinalizeEnabled = !!features?.['reArch-async-finalize'];

  const doesFinalizeNeedApproval = actionsWithActiveWorkflows.includes(
    WorkflowTypesMap.FINALIZE_PAYROLL,
  );

  const recalculateAmountMutation = useMutation({
    mutationFn: orgId ? api.runPayroll.adminRecalculatePayrollAmount(orgId) : undefined,
  });

  const { actionsApiResponse } = useGetComplianceActionDetails();

  const isTdsFilingConfigRequired = useIsTdsFilingConfigRequired();

  const isPayPtOnRunPayrollEnabled = useIsPayPtEnabled();

  if (!data) {
    return null;
  }

  const {
    isPayrollFinalized,
    numTotal,
    numFinalized,
    numFinalizedExcludingNewJoiners,
    numSkipped,
    numPaid,
    isExecutionRequested,
    isRetryable,
    isLocalOrRXBalanceSufficient,
    payrollMonth,
    payrollIds,
    isFirstPayroll,
    isPayrollBeingFinalized,
    numPendingEditRequest,
    isChangeRequestPendingOnFinalize,
    finalizationErrorCount = 0,
  } = data;

  // Visibility mirrors the Payroll Summary counts rendered directly above these
  // actions, so the numbers the admin reads off the status bar explain which
  // actions are on offer. T = total, F = finalized, S = skipped, E = executed;
  // the backend reports F/S/E as mutually exclusive buckets.
  //
  // New joiner orgs are excluded: there numFinalized also counts deferred new
  // joiners, who are paid in their delayed payment month rather than this one,
  // so a month with nothing payable would still satisfy T === F + S + E. Those
  // orgs keep the payroll-state conditions until the counts model them.
  const isCountDrivenVisibility = !isNewJoinerArrearFeatureEnabled;

  const canRequestExecution = isCountDrivenVisibility
    ? numTotal === numFinalized + numSkipped + numPaid && numFinalized > 0
    : isPayrollFinalized && numFinalizedExcludingNewJoiners > 0;

  const canMakeChanges =
    (isCountDrivenVisibility
      ? numFinalized > 0
      : !!payrollIds?.find((meta) => meta.isFinal && !meta.isBlocked)) &&
    !isPayrollBeingFinalized &&
    !isExecutionRequested &&
    !isCompliancePaymentInProgress({
      actionsApiResponse,
    });

  const canFinalizePayroll =
    (isCountDrivenVisibility
      ? numTotal - numPaid - numSkipped > 0 && numFinalized === 0
      : !!payrollIds?.some((meta) => !meta.isFinal)) &&
    !isPayrollBeingFinalized &&
    !isExecutionRequested &&
    numPendingEditRequest == 0 &&
    !isChangeRequestPendingOnFinalize;

  const canRecalculatePayrollAmount = specialPlatformPermissions.includes(
    'PLATFORM_PERMISSION_RECALCULATE_PAYROLL_AMOUNT',
  );

  const onFinalize = () => {
    openModal(
      <FinalizePayroll
        payrollMonth={payrollMonth}
        onError={errCtx?.setError}
        showMessage={message?.setMessage}
        doesFinalizeNeedApproval={doesFinalizeNeedApproval}
      />,
    );
  };

  const onFinalizeWithErrors = () => {
    openModal(
      <FinalizePayroll
        payrollMonth={payrollMonth}
        onError={errCtx?.setError}
        showMessage={message?.setMessage}
        doesFinalizeNeedApproval={doesFinalizeNeedApproval}
        isFinalizedWithErrors={true}
      />,
    );
  };

  const onUnfinalize = () => {
    openModal(<UnfinalizePayroll payrollMonth={payrollMonth} onError={errCtx?.setError} />);
  };

  const onExecute = () => {
    if (isTdsFilingConfigRequired) {
      return;
    }
    openModal(
      <ExecutePayroll
        payrollMonth={payrollMonth}
        isFirstPayroll={isFirstPayroll}
        onError={errCtx?.setError}
        showMessage={message?.setMessage}
      />,
    );
  };

  const onResumeExecution = () => {
    openModal(
      <ResumeExecution
        payrollMonth={payrollMonth}
        onError={errCtx?.setError}
        showMessage={message?.setMessage}
      />,
    );
  };

  const handleRecalculateAmount = () => {
    recalculateAmountMutation.mutate(payrollMonth);
  };

  // A run stopped for a reason the ORG can clear — today only insufficient
  // funds, where the workflow is still alive on its timer and resuming signals
  // it to carry on. Cheap and safe: nothing is re-dispatched.
  //
  // Every other stop is an internal fault the org cannot fix, and retrying
  // there SUPERSEDES — a fresh run re-dispatching money over today's cohort.
  // Those get no control at all here; MessageDisplay shows a short
  // contact-support notice, and the lever lives in the
  // RetryPayrollExecutionRun superadmin command.
  //
  // Deliberately NOT gated on isPayPtOnRunPayrollEnabled — whichever execution
  // layout the org is on, a stop they can fix must have a way forward.
  const showResumeExecutionAction = !!isRetryable && !isOrgKycRestricted && isAuthorizedToExecute;


  const showExecutePayrollAction =
    !showResumeExecutionAction &&
    !isPayPtOnRunPayrollEnabled &&
    canRequestExecution &&
    !isOrgKycRestricted &&
    !isExecutionRequested &&
    isLocalOrRXBalanceSufficient &&
    isAuthorizedToExecute;

  const showExecutionWarningsCard = isPayrollFinalized && !isExecutionRequested;
  return (
    <div className="flex flex-wrap gap-6">
      {isAuthorizedToFinalize && (
        <>
          {canMakeChanges && (
            <StandardButton type="button" onClick={onUnfinalize} className="!w-auto">
              Make Changes
            </StandardButton>
          )}
          {canFinalizePayroll && (
            <FinalizeCard
              doesFinalizeNeedApproval={doesFinalizeNeedApproval}
              isBulkAdditionDeductionInProgress={data.isBulkAdditionDeductionInProgress}
              onFinalizePayroll={onFinalize}
              onFinalizePayrollWithErrors={onFinalizeWithErrors}
              finalizationErrorCount={finalizationErrorCount}
              payrollMonth={payrollMonth}
            />
          )}
          {isAsyncFinalizeEnabled &&
            !isReArchAsyncFinalizeEnabled &&
            canMakeChanges &&
            isPayrollFinalized &&
            canRecalculatePayrollAmount &&
            recalculateAmountMutation.status !== 'success' && (
              <StandardButton
                className="mb-5 !w-auto"
                type="button"
                onClick={handleRecalculateAmount}
                isPending={recalculateAmountMutation.status === 'loading'}>
                Re-calculate
              </StandardButton>
            )}
        </>
      )}
      {showExecutionWarningsCard && <ExecutionWarningsCard />}
      {showResumeExecutionAction && (
        <StandardButton type="button" onClick={onResumeExecution} className="!w-auto">
          Resume execution
        </StandardButton>
      )}
      {showExecutePayrollAction && (
        <ExecuteCard
          isTdsFilingConfigRequired={isTdsFilingConfigRequired}
          onExecutePayroll={onExecute}
        />
      )}
    </div>
  );
};

export default PayrollActions;
